/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Logger } from '@app/core';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Industry } from '@app/settings/industries/industries.model';
import { IndustriesService } from '@app/settings/industries/industries.service';
import { IndustriesFormService } from '@app/settings/industries/industries-form.service';

const log = new Logger('IndustriesSelectComponent');

@UntilDestroy()
@Component({
  selector: 'app-industries-select',
  templateUrl: './industries-select.component.html',
  styleUrls: ['./industries-select.component.scss'],
})
export class IndustriesSelectComponent implements OnInit {
  data: Observable<Industry[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() FormControl: FormControl;
  form: FormGroup;
  input$ = new Subject<string>();
  @Input() required = false;
  env = environment;
  lastResponse: any = null;

  constructor(
    private industriesService: IndustriesService,
    private formBuilder: FormBuilder,
    private industriesFormService: IndustriesFormService
  ) {}

  ngOnInit() {
    /*
     *  създава fake formGroup, за да може да пази временно стойностите някъде
     *  после чрез евентите add, remove и clear ги прехвърля към подадения на компонента контрол
     */
    if (this.required) {
      this.form = this.formBuilder.group({
        industries_fake: [[], [Validators.required]],
      });
    } else {
      this.form = this.formBuilder.group({
        industries_fake: [[]],
      });
    }

    /*
     * това помага за patch-a на parent формата
     * ако се пали в някой друг случай трябва да се измисли нещо по-добро
     */
    this.FormControl.valueChanges.pipe(untilDestroyed(this)).subscribe((values: []) => {
      setTimeout(() => {
        /*
         *  Проверява дали всички обекти имат name
         *  Ако някой няма, то не е подаден обект от тип Events или не е пълен
         *  В такъв случай гетва на ново през сървиса всеки Event
         */
        if (values.some((x) => !x['name'])) {
          this.loadIndustriesByIds(values);
        } else {
          /*
           * Цялата тая въртележка е само за да може да си позне типа и да извика "formattedName" в темплейта
           */
          const temp: Array<Industry> = new Array<Industry>();
          values.forEach((industry: Industry) => {
            temp.push(new Industry().deserialize(industry));
          });
          this.data = of(temp);
        }

        const preSelectedValues: Array<any> = [];
        values.forEach((event: any) => {
          preSelectedValues.push(event.id);
        });
        this.form.get('industries_fake').setValue(preSelectedValues);
      });
    });

    this.eventsSearch();
  }

  loadIndustriesByIds(events: any[]) {
    this.loader = true;
    for (let i = 0; i < events.length; i++) {
      this.industriesService
        .show(events[i]['id'])
        .pipe(untilDestroyed(this))
        .subscribe((ev: Industry) => {
          this.data = of([ev]); // не знам защо работи
        });
    }
    this.loader = false;
  }

  eventsSearch() {
    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.industriesService
            .index(
              this.apiQuery.addFilters({
                active: true,
                name: term,
              })
            )
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.loader = false))
            );
        })
      )
      .subscribe((industries: PaginatedResponse) => {
        this.data = of(industries.data);
        this.lastResponse = industries;
        this.loader = false;
      });
  }

  add(event: any) {
    this.FormControl.value.push(event);
    this.FormControl.updateValueAndValidity();
  }

  remove(id: any) {
    let index: number;
    this.FormControl.value.forEach((value: any, ind: number) => {
      if (value.id === id) {
        index = ind;
      }
    });
    this.FormControl.value.splice(index, 1);
    this.FormControl.updateValueAndValidity();
  }

  clear() {
    this.FormControl.setValue([]);
    this.FormControl.updateValueAndValidity();
  }
}
